import { enqueueActions, raise } from 'xstate';

import { FlowRouteCategory } from '../types/enums';
import { FlowEvent, BaseEvent, FlowContext, FlowNavigationCategory, FlowRoute } from '../types/machine';

type State = {
  [key: string]: unknown;
};

const getNextEvent = <T extends FlowContext>(
  context: T,
  routes: FlowRoute<T>[],
  direction: 'next' | 'previous',
): FlowEvent<T> | undefined => {
  let tempRoutes = [...routes];

  if (direction !== 'next') tempRoutes.reverse();

  const indexCurrent = tempRoutes.findIndex(route => route.path === context.path) ?? 0;
  tempRoutes = tempRoutes.slice(indexCurrent + 1);

  const nextRoute = tempRoutes.find(route => {
    if (!route.skippingRules?.length) {
      return true;
    }
    return route.skippingRules.every(rule => rule(context) === false);
  });

  if (nextRoute) {
    return { type: 'GOTO', stepName: `STEP_${nextRoute.id}` };
  } else if (direction === 'next') {
    return { type: 'COMPLETE' };
  }
};

export const getEventTypeByContext = <T extends FlowContext>(routes: FlowRoute<T>[] = [], context: T): string => {
  const nextRoute = routes.find(route => {
    if (!route.skippingFields?.length) {
      return true;
    }
    return !route.skippingFields?.every(field => context[field as keyof FlowContext]);
  });

  return `STEP_${nextRoute?.id ?? routes[0]?.id}`;
};

export const getEventTypeByCategory = <T extends FlowContext>(
  routes: FlowRoute<T>[],
  category: FlowRouteCategory,
  context: T,
): string => {
  const tempRoutes = routes.filter(route => route.category === category);

  const nextRoute = tempRoutes.find(route => {
    if (!route.skippingRules?.length) {
      return true;
    }
    return route.skippingRules.every(rule => rule(context) === false);
  });

  return `STEP_${nextRoute ? nextRoute.id : routes[0]?.id}`;
};

export const getEventTypeByPath = <T extends FlowContext>(
  routes: FlowRoute<T>[] = [],
  path: string | undefined = '',
): string => `STEP_${routes.find(route => route.path === path)?.id ?? routes[0]?.id}`;

export const getNavigationCategories = <T extends FlowContext>(
  routes: FlowRoute<T>[] = [],
): FlowNavigationCategory<T>[] =>
  routes.reduce((acc: FlowNavigationCategory<T>[], route: FlowRoute<T>) => {
    const routePrevious = [...acc].pop();

    const { id, category } = route;

    if (category !== routePrevious?.category) {
      acc.push({
        id,
        category,
      });
    }

    return acc;
  }, []);

export const generateStateTransitions = <T extends FlowContext>(routes: FlowRoute<T>[] = []) =>
  routes.map(route => {
    const routeId = `STEP_${route.id}`;
    return {
      target: `.${routeId}`,
      guard: ({ event }: { event: FlowEvent<T> }) => {
        return event.type === 'GOTO' && event.stepName === routeId;
      },
    };
  });

export const generateStates = <T extends FlowContext>(routes: FlowRoute<T>[] = []) =>
  routes.reduce((acc: State, route, index) => {
    const hasNextPage = index !== routes.length - 1;
    const hasPreviousPage = !!routes[index - 1];

    acc[`STEP_${route.id}`] = {
      entry: raise({
        type: 'UPDATE_ROUTE',
        values: {
          path: route.path,
          pathCategory: route.category,
          hasPreviousPath: hasPreviousPage,
        },
      } as FlowEvent<BaseEvent>),
      on: {
        NEXT_PAGE: {
          ...(!hasNextPage && {
            target: 'COMPLETED',
          }),
          actions: enqueueActions(({ context, event, enqueue }) => {
            enqueue('setValues');
            const { actions = [] } = route;
            actions.forEach(action => enqueue(action));
            const updatedContext = { ...context, ...(event as { values?: Partial<T> }).values };
            const nextEvent = getNextEvent(updatedContext as T, routes, 'next');
            if (nextEvent) {
              enqueue.raise(nextEvent);
            }
          }),
        },
        ...(hasPreviousPage && {
          PREVIOUS_PAGE: {
            actions: enqueueActions(({ context, enqueue }) => {
              const nextEvent = getNextEvent(context as T, routes, 'previous');
              if (nextEvent) {
                enqueue.raise(nextEvent);
              }
            }),
          },
        }),
      },
    };

    return acc;
  }, {});

/**
 * Generates a basket id cookie name based on a flow name
 * @param flowName - The name of the flow
 * @param suffix - Optional suffix (default: 'basket')
 * @returns cookie name
 */
export function generateBasketIdCookieName(flowName: string, suffix: string = 'basket'): string {
  return `${flowName.toLowerCase()}_${suffix}`;
}

export const getBasketIdFromUrl = (basketCookieName: string): string => {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.href);
  return url.searchParams.get(basketCookieName) ?? '';
};
